import { Component, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CONTRACT_MODIFICATION_LABELS } from '../contracts/contract-options';

interface TransactionCodeItem { id: number; shortDescription: string; defaultAmount: number; }

export interface CreateContractData {
  customerId: number;
  transactionCodes: TransactionCodeItem[];
  /** Deferred mode (New Customer page): return the payload instead of posting —
   *  the caller creates the contract once the customer exists. */
  deferred?: boolean;
}

@Component({
  selector: 'app-create-contract-dialog',
  templateUrl: './create-contract-dialog.component.html',
  styleUrls: ['./create-contract-dialog.component.css'],
})
export class CreateContractDialogComponent {
  form: any = {
    transactionCodeId: '', price: null,
    // Effective From defaults to today so a draft is one click away; both dates are
    // required by the backend (DateOnly, not nullable), so we never submit null.
    effectiveFrom: CreateContractDialogComponent.todayIso(), effectiveTo: null,
    noExpiry: false, poReleaseNumber: '', signedDocumentUrl: '',
    billingMethod: 'PerService', billingDayOfMonth: 1,
    allowedModificationTypes: [] as string[],
    title: '', terms: '',
  };
  saving = false;
  error = '';
  loadingTerms = false;

  // One list with the contract table, so the dialog that sets these and the
  // column that shows them cannot name them differently.
  readonly modTypes = Object.keys(CONTRACT_MODIFICATION_LABELS);
  readonly modTypeLabels = CONTRACT_MODIFICATION_LABELS;

  constructor(
    private http: HttpClient,
    private dialogRef: MatDialogRef<CreateContractDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateContractData,
    private snackBar: MatSnackBar,
  ) {}

  private static todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  selectedTcDefault(): string {
    const tc = this.data.transactionCodes.find(t => t.id === +this.form.transactionCodeId);
    return tc ? `TC default: $${tc.defaultAmount}` : '';
  }

  // Backend requires a transaction code and a valid date range (an end date or "no expiry").
  canCreate(): boolean {
    return !!this.form.transactionCodeId
        && !!this.form.effectiveFrom
        && (this.form.noExpiry || !!this.form.effectiveTo);
  }

  onNoExpiryChange(): void { if (this.form.noExpiry) this.form.effectiveTo = null; }

  isModTypeEnabled(t: string): boolean { return (this.form.allowedModificationTypes ?? []).includes(t); }
  toggleModType(t: string): void {
    const arr: string[] = this.form.allowedModificationTypes ?? [];
    const i = arr.indexOf(t);
    if (i >= 0) arr.splice(i, 1); else arr.push(t);
    this.form.allowedModificationTypes = [...arr];
  }

  loadStandardTerms(): void {
    if (!this.form.transactionCodeId) {
      this.snackBar.open('Pick a transaction code first.', 'OK', { duration: 3000 });
      return;
    }
    this.loadingTerms = true;
    const price = this.form.price ? `&price=${+this.form.price}` : '';
    const url = `/api/contracts/terms-template?customerId=${this.data.customerId}&transactionCodeId=${+this.form.transactionCodeId}${price}`;
    this.http.get<{ terms: string }>(url).subscribe({
      next: r => { this.form.terms = r.terms; this.loadingTerms = false; },
      error: () => { this.loadingTerms = false; this.snackBar.open('Could not load standard terms.', 'OK', { duration: 3000 }); },
    });
  }

  private isoDate(d: any): string | null {
    if (!d) return null;
    if (typeof d === 'string') return d.slice(0, 10);
    const dt = d as Date;
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  }

  create(): void {
    this.saving = true;
    this.error = '';
    const payload = {
      customerId: this.data.customerId,
      transactionCodeId: +this.form.transactionCodeId,
      price: this.form.price !== '' && this.form.price !== null ? +this.form.price : null,
      poReleaseNumber: this.form.poReleaseNumber || null,
      effectiveFrom: this.isoDate(this.form.effectiveFrom),
      effectiveTo: this.form.noExpiry ? '9999-12-31' : this.isoDate(this.form.effectiveTo),
      signedDocumentUrl: this.form.signedDocumentUrl || null,
      allowedModificationTypes: this.form.allowedModificationTypes ?? [],
      billingMethod: this.form.billingMethod,
      billingDayOfMonth: +this.form.billingDayOfMonth,
      title: this.form.title || null,
      terms: this.form.terms || null,
    };
    if (this.data.deferred) {
      const tc = this.data.transactionCodes.find(t => t.id === payload.transactionCodeId);
      this.saving = false;
      this.dialogRef.close({ deferred: payload, label: tc?.shortDescription ?? `TC ${payload.transactionCodeId}` });
      return;
    }
    this.http.post('/api/contracts', payload).subscribe({
      next: () => { this.saving = false; this.dialogRef.close(true); },
      error: (e: any) => { this.saving = false; this.error = this.errorMessage(e); },
    });
  }

  // Pull a useful message from either our { error } shape or an ASP.NET
  // validation ProblemDetails ({ errors: { field: [msg] } }).
  private errorMessage(e: any): string {
    if (e?.error?.error) return e.error.error;
    const errs = e?.error?.errors;
    if (errs && typeof errs === 'object') {
      const first = Object.values(errs)[0] as any;
      if (Array.isArray(first) && first.length) return first[0];
    }
    return e?.error?.title || 'Failed to create';
  }

  cancel(): void { this.dialogRef.close(false); }
}
